import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, Package, MapPin, Clock, CheckCircle, XCircle, Truck, 
  CreditCard, Phone, Mail, Calendar, DollarSign, Info, User, X
} from 'lucide-react'
import { api } from '../../../lib/api'
import { useAuth } from '../../../contexts/AuthContext'
import toast from 'react-hot-toast'
import { Order, OrderItem } from '../../../types'
import ProductImage from '../../../components/ProductImage'
import { getCache, getCacheStale, setCache } from '../../../lib/cache'
import { showOfflineToast } from '../../../lib/toastManager'

interface Employee {
  id: string
  name: string
  email: string
  type: 'chef' | 'delivery' | 'dispatcher' | 'store_admin'
  is_active: boolean
}

const StoreOrderDetails = () => {
  const { storeId, orderId } = useParams<{ storeId: string, orderId: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuth()
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [deliveryDrivers, setDeliveryDrivers] = useState<Employee[]>([])
  const [showAssignDeliveryModal, setShowAssignDeliveryModal] = useState(false)
  const [assigningDelivery, setAssigningDelivery] = useState(false)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [lastScrollY, setLastScrollY] = useState(0)
  
  // Refs para prevenir duplicados en React StrictMode
  const loadingOrderRef = useRef(false)
  const loadingDriversRef = useRef(false)

  // Hook para ocultar/mostrar header en scroll (solo pantallas pequeñas)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY
      const isSmallScreen = window.innerWidth < 640

      if (!isSmallScreen) {
        setIsHeaderVisible(true)
        return
      }

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false)
      } else if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true)
      }

      setLastScrollY(currentScrollY)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [lastScrollY])

  useEffect(() => {
    if (isAuthenticated && orderId) {
      if (!loadingOrderRef.current) {
        loadingOrderRef.current = true
        loadOrder()
      }
      // Solo cargar delivery drivers si hay storeId y el usuario no es delivery
      if (storeId && user?.type !== 'delivery' && !loadingDriversRef.current) {
        loadingDriversRef.current = true
        loadDeliveryDrivers()
      }
    }
    
    return () => {
      loadingOrderRef.current = false
      loadingDriversRef.current = false
    }
  }, [isAuthenticated, orderId, storeId, user?.type])

  const loadDeliveryDrivers = async () => {
    if (!storeId) return
    
    // Validar que storeId sea un UUID válido (no "delivery" u otros valores)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    if (!uuidRegex.test(storeId)) {
      return // No intentar cargar si storeId no es un UUID válido
    }

    const cacheKey = `store_delivery_drivers_${storeId}`
    
    // Intentar cargar desde cache primero
    const cachedDrivers = getCacheStale<Employee[]>(cacheKey)
    if (cachedDrivers && cachedDrivers.length > 0) {
      setDeliveryDrivers(cachedDrivers)
      // Si tenemos datos válidos en cache, no hacer petición HTTP
      return
    }

    try {
      const response = await api.get(`/auth/store/${storeId}/employees`)
      if (response.data.success) {
        const employees = response.data.data || []
        const drivers = employees.filter((e: Employee) => e.type === 'delivery' && e.is_active)
        setDeliveryDrivers(drivers)
        
        // Guardar en cache (10 minutos de TTL)
        setCache(cacheKey, drivers, 10 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading delivery drivers:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedDrivers) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      // Silenciar el error si el usuario es delivery o no tiene permisos
      if (user?.type === 'delivery') {
        return
      }
    } finally {
      loadingDriversRef.current = false
    }
  }

  const handleAssignDelivery = async (driverId: string) => {
    if (!order) return

    try {
      setAssigningDelivery(true)
      const response = await api.patch(`/orders/${order.id}/status`, {
        status: order.status, // Mantener el estado actual
        assignedDeliveryId: driverId
      })
      
      if (response.data.success) {
        toast.success('Repartidor asignado exitosamente')
        setShowAssignDeliveryModal(false)
        loadOrder() // Reload to get updated assignment
      } else {
        toast.error(response.data.message || 'Error al asignar repartidor')
      }
    } catch (error: any) {
      console.error('Error assigning delivery:', error)
      toast.error(error.response?.data?.message || 'Error al asignar repartidor')
    } finally {
      setAssigningDelivery(false)
    }
  }

  const loadOrder = async () => {
    if (!orderId) return
    
    const cacheKey = `store_order_details_${orderId}`
    
    // Intentar cargar desde cache primero
    const cachedOrder = getCacheStale<Order>(cacheKey)
    if (cachedOrder) {
      setOrder(cachedOrder)
      setLoading(false)
      // Si tenemos datos válidos en cache, hacer petición en background para actualizar
      // pero no bloquear la UI
    }

    try {
      setLoading(true)
      const response = await api.getOrder(orderId!)
      if (response.success && response.data) {
        // Normalizar los datos de la orden
        const orderData = response.data
        const normalizedOrder: Order = {
          id: orderData.id,
          storeId: orderData.store_id || orderData.storeId,
          customerName: orderData.customer_name || orderData.customerName,
          customerPhone: orderData.customer_phone || orderData.customerPhone,
          customerEmail: orderData.customer_email || orderData.customerEmail,
          status: orderData.status,
          totalAmount: Number(orderData.total_amount || orderData.totalAmount) || 0,
          deliveryFee: Number(orderData.delivery_fee || orderData.deliveryFee) || 0,
          taxAmount: Number(orderData.tax_amount || orderData.taxAmount) || 0,
          estimatedDeliveryTime: Number(orderData.estimated_delivery_time || orderData.estimatedDeliveryTime) || 0,
          notes: orderData.notes,
          paymentMethod: orderData.payment_method || orderData.paymentMethod,
          paymentStatus: orderData.payment_status || orderData.paymentStatus,
          createdAt: orderData.created_at || orderData.createdAt,
          assignedDeliveryId: orderData.assigned_delivery_id || orderData.assignedDeliveryId,
          items: (orderData.items || []).map((item: any) => ({
            id: item.id,
            productId: item.product_id || item.productId,
            variantId: item.variant_id || item.variantId,
            quantity: Number(item.quantity) || 0,
            unitPrice: Number(item.unit_price || item.unitPrice) || 0,
            notes: item.notes,
            variant: item.variant ? {
              id: item.variant.id,
              variant_name: item.variant.variant_name,
              variant_type: item.variant.variant_type,
              stock_quantity: Number(item.variant.stock_quantity) || 0,
              is_available: item.variant.is_available !== false,
              sku: item.variant.sku,
              image_url: item.variant.image_url
            } : undefined,
            product: item.product ? {
              id: item.product.id || item.productId,
              name: item.product.name || 'Producto',
              description: item.product.description,
              price: Number(item.product.price || item.unitPrice) || 0,
              imageUrl: item.product.image_url || item.product.imageUrl,
              categoryId: item.product.category_id || '',
              storeId: orderData.store_id || orderData.storeId,
              isAvailable: item.product.is_available !== false,
              stockQuantity: Number(item.product.stock_quantity || 999) || 999,
              unit: item.product.unit || 'unidad',
              isFeatured: false
            } : undefined
          })),
          store: orderData.store ? {
            id: orderData.store_id || orderData.storeId,
            name: orderData.store.name || 'Negocio',
            slug: orderData.store.slug || '',
            address: orderData.store.address || '',
            phone: orderData.store.phone,
            email: '',
            latitude: 0,
            longitude: 0,
            status: 'approved' as const,
            isActive: true,
            offersKitchenService: false,
            offersDelivery: true,
            deliveryFee: Number(orderData.delivery_fee || orderData.deliveryFee) || 0,
            minOrderAmount: 0,
            ownerId: ''
          } : undefined
        }
        setOrder(normalizedOrder)
        
        // Guardar en cache (2 minutos de TTL - órdenes dinámicas pero no tan frecuentes)
        setCache(cacheKey, normalizedOrder, 2 * 60 * 1000)
      } else {
        toast.error('Pedido no encontrado')
        navigate(`/store/${storeId}/orders`)
      }
    } catch (error: any) {
      console.error('Error loading order:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrder) {
        showOfflineToast()
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      // Solo mostrar error si no hay datos en cache
      if (!cachedOrder) {
        toast.error(error.response?.data?.message || 'Error al cargar el pedido')
        navigate(`/store/${storeId}/orders`)
      }
    } finally {
      setLoading(false)
      loadingOrderRef.current = false
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    if (!order) return

    // Validar que no se pueda cambiar a 'delivering' sin assigned_delivery_id
    if (newStatus === 'delivering' && !order.assignedDeliveryId) {
      toast.error('No se puede marcar como "En Camino" sin asignar un repartidor')
      return
    }

    if (!confirm(`¿Estás seguro de cambiar el estado a "${getStatusText(newStatus)}"?`)) {
      return
    }

    try {
      setUpdatingStatus(true)
      const response = await api.patch(`/orders/${order.id}/status`, { status: newStatus })
      if (response.data.success) {
        toast.success(`Estado actualizado a "${getStatusText(newStatus)}"`)
        loadOrder() // Reload to get updated status
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al actualizar el estado del pedido')
    } finally {
      setUpdatingStatus(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'preparing':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'ready':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'delivering':
        return 'bg-orange-100 text-orange-800 border-orange-200'
      case 'delivered':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente'
      case 'preparing':
        return 'Preparando'
      case 'ready':
        return 'Listo'
      case 'delivering':
        return 'En Camino'
      case 'delivered':
        return 'Entregado'
      case 'cancelled':
        return 'Cancelado'
      default:
        return status
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
      case 'preparing':
        return <Clock className="w-5 h-5 sm:w-6 sm:h-6" />
      case 'ready':
        return <Package className="w-5 h-5 sm:w-6 sm:h-6" />
      case 'delivering':
        return <Truck className="w-5 h-5 sm:w-6 sm:h-6" />
      case 'delivered':
        return <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
      case 'cancelled':
        return <XCircle className="w-5 h-5 sm:w-6 sm:h-6" />
      default:
        return <Package className="w-5 h-5 sm:w-6 sm:h-6" />
    }
  }

  const getNextStatuses = (currentStatus: string): string[] => {
    // Si es admin de plataforma, puede revertir estados (ir hacia atrás)
    const isPlatformAdmin = user?.type === 'platform_admin'
    
    switch (currentStatus) {
      case 'pending': 
        return ['preparing', 'cancelled']
      case 'preparing': 
        return ['ready', 'cancelled', ...(isPlatformAdmin ? ['pending'] : [])]
      case 'ready': 
        return ['delivering', 'cancelled', ...(isPlatformAdmin ? ['preparing', 'pending'] : [])]
      case 'delivering': 
        return ['delivered', ...(isPlatformAdmin ? ['ready', 'preparing', 'pending', 'cancelled'] : [])]
      case 'delivered': 
        return isPlatformAdmin ? ['delivering', 'ready', 'preparing', 'pending', 'cancelled'] : []
      case 'cancelled': 
        return isPlatformAdmin ? ['pending', 'preparing', 'ready'] : []
      default: 
        return []
    }
  }

  if (!isAuthenticated) {
    return null
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center pb-20">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl mb-6">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
          </div>
          <p className="text-gray-600 font-semibold">Cargando pedido...</p>
        </div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 flex items-center justify-center pb-20">
        <div className="text-center max-w-md w-full px-4">
          <div className="bg-white/70 backdrop-blur-2xl rounded-xl sm:rounded-2xl shadow-2xl border border-white/20 p-12">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-red-100 to-red-200 rounded-xl sm:rounded-2xl mb-8 shadow-xl">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Pedido no encontrado</h2>
            <p className="text-gray-600 mb-8 font-medium">El pedido que buscas no existe o no tienes acceso a él</p>
            <button
              onClick={() => navigate(`/store/${storeId}/orders`)}
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 active:scale-95"
            >
              Volver a Pedidos
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40 pb-20 sm:pb-8">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12">
        {/* Header - Ocultable en scroll en pantallas pequeñas */}
        <div className={`bg-white/40 backdrop-blur-2xl border-b border-white/20 shadow-lg sticky top-0 z-50 mb-4 sm:mb-6 lg:mb-8 transition-transform duration-300 ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}>
          <div className="py-3 sm:py-4 lg:py-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                onClick={() => navigate(`/store/${storeId}/orders`)}
                className="p-2.5 sm:p-3 rounded-xl sm:rounded-2xl bg-white/60 backdrop-blur-md hover:bg-white/80 border border-white/30 shadow-md hover:shadow-lg transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-1 sm:mb-2 truncate">
                  Detalles del Pedido
                </h1>
                <p className="text-xs sm:text-sm text-gray-600 font-semibold">#{order.id.slice(0, 8)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Status Card - Compacto */}
        <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6 lg:mb-8">
          <div className="flex items-center justify-between mb-4 sm:mb-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center border-2 ${getStatusColor(order.status)}`}>
                {getStatusIcon(order.status)}
              </div>
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Estado del Pedido</h2>
                <p className={`text-sm sm:text-base font-semibold ${getStatusColor(order.status).split(' ')[1]}`}>
                  {getStatusText(order.status)}
                </p>
              </div>
            </div>
            {order.estimatedDeliveryTime && order.status !== 'delivered' && order.status !== 'cancelled' && (
              <div className="text-right">
                <p className="text-xs sm:text-sm text-gray-500">Tiempo estimado</p>
                <p className="text-base sm:text-lg font-bold text-gray-900">{order.estimatedDeliveryTime} min</p>
              </div>
            )}
          </div>

          {/* Assign Delivery Button for Ready Orders - Only for store owners, admins, and dispatchers */}
          {order.status === 'ready' && user && ['store_owner', 'store_admin', 'dispatcher'].includes(user.type) && (
            <div className="mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-gray-200">
              {order.assignedDeliveryId ? (
                <div className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-xl sm:rounded-2xl">
                  <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  <div className="flex-1">
                    <p className="text-sm sm:text-base font-semibold text-green-900">Repartidor Asignado</p>
                    <p className="text-xs sm:text-sm text-green-700">
                      {deliveryDrivers.find(d => d.id === order.assignedDeliveryId)?.name || 'Repartidor asignado'}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAssignDeliveryModal(true)}
                    className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-green-700 hover:text-green-900 hover:bg-green-100 rounded-lg transition-colors"
                  >
                    Cambiar
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAssignDeliveryModal(true)}
                  disabled={deliveryDrivers.length === 0}
                  className="w-full flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-5 py-3 sm:py-3.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl sm:rounded-2xl text-sm sm:text-base font-semibold transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                >
                  <Truck className="w-5 h-5 sm:w-6 sm:h-6" />
                  {deliveryDrivers.length === 0 ? 'No hay repartidores disponibles' : 'Asignar Repartidor'}
                </button>
              )}
            </div>
          )}

          {/* Status Change Buttons */}
          {getNextStatuses(order.status).length > 0 && (
            <div className="flex flex-wrap gap-2 sm:gap-3 mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-gray-200">
              {getNextStatuses(order.status).map((nextStatus) => (
                <button
                  key={nextStatus}
                  onClick={() => handleStatusChange(nextStatus)}
                  disabled={updatingStatus}
                  className={`px-4 sm:px-5 py-2 sm:py-3 rounded-xl sm:rounded-2xl text-sm sm:text-base font-semibold transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${
                    nextStatus === 'cancelled'
                      ? 'bg-red-50 text-red-700 hover:bg-red-100 border-2 border-red-200'
                      : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-lg'
                  }`}
                >
                  {updatingStatus ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                      Actualizando...
                    </span>
                  ) : (
                    <>
                      {nextStatus === 'preparing' && 'Preparar'}
                      {nextStatus === 'ready' && 'Marcar Listo'}
                      {nextStatus === 'delivering' && 'Enviar'}
                      {nextStatus === 'delivered' && 'Marcar Entregado'}
                      {nextStatus === 'cancelled' && 'Cancelar'}
                    </>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Customer Info */}
        <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6 lg:mb-8">
          <h2 className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 lg:mb-5 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl sm:rounded-2xl flex items-center justify-center">
              <User className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            Información del Cliente
          </h2>
          <div className="p-4 sm:p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl border border-gray-200">
            <p className="font-semibold text-gray-900 text-base sm:text-lg mb-2">{order.customerName}</p>
            {order.customerPhone && (
              <p className="text-sm sm:text-base text-gray-600 flex items-center gap-2 mb-1">
                <Phone className="w-4 h-4" />
                {order.customerPhone}
              </p>
            )}
            {order.customerEmail && (
              <p className="text-sm sm:text-base text-gray-600 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {order.customerEmail}
              </p>
            )}
          </div>
        </div>

        {/* Order Items */}
        <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-8 mb-8">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-5">Productos</h2>
          <div className="space-y-3 sm:space-y-4">
            {order.items.map((item: OrderItem) => (
              <div
                key={item.id}
                className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl border border-gray-200"
              >
                <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm">
                  <ProductImage
                    imageKey={item.variant?.image_url || item.product?.imageUrl || null}
                    alt={item.product?.name || 'Producto'}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-sm sm:text-base lg:text-lg mb-1 line-clamp-2">
                    {item.product?.name || 'Producto'}
                    {item.variant && (
                      <span className="text-primary-600 font-normal ml-2">
                        - {item.variant.variant_name}
                      </span>
                    )}
                  </h3>
                  {item.product?.description && (
                    <p className="text-xs sm:text-sm text-gray-600 line-clamp-1 mb-2">
                      {item.product.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-xs sm:text-sm text-gray-500">
                      Cantidad: {item.quantity} {item.product?.unit || 'unidad'}
                    </span>
                    <span className="text-sm sm:text-base lg:text-lg font-bold text-primary-600">
                      RD${(item.quantity * (Number(item.unitPrice) || 0)).toFixed(2)}
                    </span>
                  </div>
                  {item.notes && (
                    <p className="text-xs sm:text-sm text-gray-500 mt-2 italic">
                      Nota: {item.notes}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery Address */}
        {order.store && (
          <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-8 mb-8">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-4 sm:mb-5 flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-xl sm:rounded-2xl flex items-center justify-center">
                <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              Dirección de Entrega
            </h2>
            <div className="p-4 sm:p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl border border-gray-200">
              <p className="text-sm sm:text-base text-gray-700">
                {order.store.address}
              </p>
            </div>
          </div>
        )}

        {/* Payment & Order Info */}
        <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6 lg:mb-8">
          <h2 className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 lg:mb-5 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl sm:rounded-2xl flex items-center justify-center">
              <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            Información de Pago
          </h2>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600">Método de Pago</span>
              <span className="font-semibold text-gray-900">
                {order.paymentMethod === 'cash' ? 'Efectivo' : order.paymentMethod === 'card' ? 'Tarjeta' : order.paymentMethod || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600">Estado de Pago</span>
              <span className={`font-semibold ${
                order.paymentStatus === 'paid' ? 'text-green-600' :
                order.paymentStatus === 'pending' ? 'text-yellow-600' :
                'text-gray-600'
              }`}>
                {order.paymentStatus === 'paid' ? 'Pagado' : order.paymentStatus === 'pending' ? 'Pendiente' : order.paymentStatus || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Fecha del Pedido
              </span>
              <span className="font-semibold text-gray-900">
                {new Date(order.createdAt).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            {order.notes && (
              <div className="pt-3 sm:pt-4 border-t border-gray-200">
                <p className="text-xs sm:text-sm text-gray-500 mb-1 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Notas del Pedido
                </p>
                <p className="text-sm sm:text-base text-gray-700">{order.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Order Summary */}
        <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/20 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6 lg:mb-8">
          <h2 className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 lg:mb-5 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl sm:rounded-2xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
            </div>
            Resumen del Pedido
          </h2>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-semibold text-gray-900">
                RD${((Number(order.totalAmount) || 0) - (Number(order.deliveryFee) || 0) - (Number(order.taxAmount) || 0)).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600">Entrega</span>
              <span className="font-semibold text-gray-900">RD${(Number(order.deliveryFee) || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm sm:text-base">
              <span className="text-gray-600">ITBIS (18%)</span>
              <span className="font-semibold text-gray-900">RD${(Number(order.taxAmount) || 0).toFixed(2)}</span>
            </div>
            <div className="border-t-2 border-gray-200 pt-4 sm:pt-5 flex justify-between">
              <span className="font-bold text-gray-900 text-base sm:text-lg">Total</span>
              <span className="font-bold text-primary-600 text-xl sm:text-2xl lg:text-3xl">
                RD${(Number(order.totalAmount) || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Assign Delivery Modal */}
        {showAssignDeliveryModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <div className="bg-white/80 backdrop-blur-2xl rounded-xl sm:rounded-2xl max-w-md w-full shadow-2xl border border-white/30">
              <div className="sticky top-0 bg-white/60 backdrop-blur-xl border-b border-white/30 px-6 py-5 flex items-center justify-between rounded-t-[2.5rem]">
                <h3 className="text-2xl font-bold text-gray-900">Asignar Repartidor</h3>
                <button
                  onClick={() => setShowAssignDeliveryModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors active:scale-95 p-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                {deliveryDrivers.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100/80 rounded-xl sm:rounded-2xl mb-6">
                      <Truck className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-600 mb-2 font-semibold">No hay repartidores disponibles</p>
                    <p className="text-sm text-gray-500 font-medium">Agrega repartidores en la sección de empleados</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {deliveryDrivers.map((driver) => (
                      <button
                        key={driver.id}
                        onClick={() => handleAssignDelivery(driver.id)}
                        disabled={assigningDelivery}
                        className="w-full flex items-center gap-4 p-5 bg-white/60 backdrop-blur-sm hover:bg-white/80 rounded-2xl border border-white/30 transition-all duration-300 disabled:opacity-50 active:scale-95 shadow-lg hover:shadow-xl"
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                          <Truck className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-bold text-gray-900">{driver.name}</p>
                          <p className="text-sm text-gray-600 font-medium">{driver.email}</p>
                        </div>
                        {assigningDelivery && (
                          <div className="animate-spin rounded-full h-6 w-6 border-4 border-blue-200 border-t-blue-600"></div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default StoreOrderDetails

